import { isAppLut } from 'legacy/lut/functions'
import { memo, useCallback, useMemo, useState } from 'react'

import { IPartner, IPartnerFlags } from 'clickNgo/entities/partner/models'
import { PartnerEditor } from 'clickNgo/entities/partner/ui/partnerEditor'
import { IAttachment } from 'clickNgo/models/file'
import { MY_CONNECT_TYPE } from 'dialer/classes/models/MyConnectType'
import {
  CALL_MODE,
  CallablePhoneWrapper,
  getFullPhone,
} from 'dialer/components/widgets/public/callPhoneWrapper/CallablePhoneWrapper'

import { HandlerProps } from '../../models'
import { companyDetailsTranslations } from '../../translation'

import { ActionIcon } from './components/actionIcon'
import { getTelLink, getUrl } from './helpers'

import styles from './CompanyDetailsActions.module.scss'

type Props = {
  partner: IPartner
  partnerFlags: IPartnerFlags
  partnerAttachments: IAttachment[]
} & HandlerProps

export const CompanyDetailsActions = memo<Props>(
  ({
    partner,
    partnerFlags,
    partnerAttachments,
    onPartnerUpdate,
    onPartnerFlagsUpdate,
    onPartnerAttachmentsUpdate,
    isReadOnly,
  }) => {
    const [isEditPartnerDialogVisible, setIsEditPartnerDialogVisible] = useState(false)

    const partnerUrl = getUrl(partner.url)
    const partnerPhone = useMemo(
      () => getFullPhone(partner.phoneCountryCode, partner.phone) || partner.phone,
      [partner.phone, partner.phoneCountryCode]
    )
    const partnerTel = useMemo(
      () => getTelLink({ guid: partner.hpdPartnerGuid, phone: partnerPhone }),
      [partner.hpdPartnerGuid, partnerPhone]
    )
    const isPhoneActionActive = Boolean(partnerPhone)

    const onEditPartnerDialogOpenHandler = useCallback(() => {
      setIsEditPartnerDialogVisible(true)
    }, [])

    const onEditPartnerDialogCloseHandler = useCallback(() => {
      setIsEditPartnerDialogVisible(false)
    }, [])

    return (
      <div className={styles.container}>
        <ActionIcon
          id="cmp-URL"
          href={partnerUrl}
          text={companyDetailsTranslations.actions.website}
          iconClassName="hpicon hpicon-social-website"
          isActive={Boolean(partner.url)}
        />
        {isPhoneActionActive ? (
          <CallablePhoneWrapper
            className={styles.phoneActionWrapper}
            callMode={CALL_MODE.byPhoneClick}
            phone={partnerPhone}
            type={MY_CONNECT_TYPE.Partner}
            additionalAttributes={{
              partnerId: partner.partnerId,
              contactProcessedInScout: isAppLut(),
            }}
          >
            <ActionIcon
              id="cmp-Phone"
              href={partnerTel}
              text={companyDetailsTranslations.actions.phoneCall}
              iconClassName="hpicon hpicon-telephone2"
            />
          </CallablePhoneWrapper>
        ) : (
          <ActionIcon
            id="cmp-Phone"
            text={companyDetailsTranslations.actions.phoneCall}
            iconClassName="hpicon hpicon-telephone2"
            isActive={false}
          />
        )}
        <ActionIcon
          id="cmp-Edit"
          onClick={onEditPartnerDialogOpenHandler}
          text="jbp-partner-details-cog"
          iconClassName="kbase kbase-settings-alt"
        />
        <PartnerEditor
          isVisible={isEditPartnerDialogVisible}
          isReadOnly={isReadOnly}
          partner={partner}
          partnerFlags={partnerFlags}
          partnerAttachments={partnerAttachments}
          onClose={onEditPartnerDialogCloseHandler}
          onPartnerUpdate={onPartnerUpdate}
          onPartnerFlagsUpdate={onPartnerFlagsUpdate}
          onPartnerAttachmentsUpdate={onPartnerAttachmentsUpdate}
        />
      </div>
    )
  }
)

CompanyDetailsActions.displayName = 'CompanyDetailsActions'
